import React from "react";
import { Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import IosShareIcon from '@mui/icons-material/IosShare';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { ResourceAction, ResourceChange } from "./ChangeLog";
import { currentUser } from "system/InfegyUser";
import ISODateTime from "components/formatting/ISODateTime";

interface ResourceChangeLogRowDetailsProps {
  change: ResourceChange;
}

export default function ResourceChangeLogRowDetails(props: ResourceChangeLogRowDetailsProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const me = currentUser();

  function getActionIcon(action: ResourceAction) {
    switch (action) {
      case "create": {
        return <AddCircleIcon color="success" />;
      }
      case "duplicate": {
        return <ContentCopyIcon color="primary" />
      }
      case "remove": {
        return <DeleteForeverIcon color="error" />;
      }
      case "share": {
        return <IosShareIcon color="primary" />;
      }
      case "update": {
        return <EditIcon color="primary" />;
      }
      default: {
        return <ChangeCircleIcon color="primary" />;
      }
    }
  }

  const change = props.change;

  const time = i18n.t('intlDateTime', {
    val: new Date(change.timestamp),
    formatParams: {
      val: {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        // hour12: false
      }
    },
  });

  let lastUpdateUser = i18n.t("you");
  if (!props.change.user_email) {
    lastUpdateUser = i18n.t("someone-else");
  } else if (props.change.user_email !== me?.email) {
    lastUpdateUser = props.change.user_email;
  }

  return (
    <Paper variant="outlined" sx={{p: 1}}>
      <Stack direction="row" sx={{gap: 1, alignItems: "center"}}>
        {getActionIcon(change.action)}
        <Stack sx={{width: "100%"}}>
          <Tooltip followCursor title={<ISODateTime isoString={change.timestamp} hideTooltip={true} />}>
            <Typography>{time}</Typography>
          </Tooltip>
          <Typography>
            <Trans
              i18nKey="change-log-row-details.summary"
              values={{
                action: i18n.t(`change-log.action.${change.action}`),
                person: lastUpdateUser,
                resource: i18n.t(`change-log.resource-type.${change.resource_type}`)
              }}
            />
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}